import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { IBreadcrumbs } from 'libs/design-system/src/types/breadcrumbs';
import {
  Users,
  LinkSimple,
  PenToSquare,
  CodeSimple,
  FileContract,
  Bell,
  User,
  Suitcase,
  Grid2Plus,
  Bolt,
} from '@unified-checkout/design-system/icons/index';

import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { PROFILE } from '@app/dashboard/constants/profile';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { UNIFIED_DOCS_URL } from '@app/dashboard/utils/serverUrls';
import { getProfileType } from '@app/dashboard/utils/localStorageUtils';
import { getKeysPermissions, checkIfStaff } from '../ApiKeys/utils';

const SETTINGS_INTERNAL_PAGES = {
  PROFILE_DETAILS: 'profile_details',
  CHANGE_PASSWORD: 'change_password',
  BUSINESS_PROFILE: 'business_profile',
  USER_AND_PERMISSIONS: 'user_and_permissions',
  NOTIFICATIONS: 'notifications',
  CHECKOUT_CUSTOMIZATION: 'checkout_customization',
  SERVICES: 'services',
  PAYMENT_INTEGRATIONS: 'payment_integrations',
  SINGLE_PAYMENT_INTEGRATION: 'single_payment_integration',
  IFRAMES: 'iframes',
  API_KEYS: 'api_keys',
  XERO: 'xero'
};

export const useSettingsUtils = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    SETTINGS,
    PROFILE_DETAILS,
    BUSINESS_PROFILE,
    USER_AND_PERMISSIONS,
    NOTIFICATIONS,
    CHECKOUT_CUSTOMIZATION,
    SERVICES,
    PAYMENT_INTEGRATIONS,
    IFRAMES,
    API_KEYS,
    XERO
  } = INNER_ROUTES;

  const sharedRoute = {
    text: t('innerLayout.settingsPage.breadcrumbs.settings'),
    onClickHandler: () => navigate(SETTINGS),
    active: true
  };

  const isMerchant = getProfileType() === PROFILE.TYPE.MERCHANT;

  const canViewUserManagement = isAccessible(PERMISSIONS.CAN_VIEW_STAFF);
  const canViewPaymentIntegrations = isAccessible(PERMISSIONS.CAN_VIEW_PAYMENT_INTEGRATIONS);
  const canViewIframes = isAccessible(PERMISSIONS.CAN_VIEW_IFRAMES);
  const canViewNotifications = getProfileType() === PROFILE.TYPE.MERCHANT;
  const hideApiKeys = Object.values(getKeysPermissions()).every(permission => !permission) && checkIfStaff();    

  const internalPagesBreadcrumbs: Record<keyof typeof SETTINGS_INTERNAL_PAGES, (IBreadcrumbs['linksProps'])> = {
    BUSINESS_PROFILE: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.businessProfile'),
        active: false
      }
    ],
    PROFILE_DETAILS: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.profileDetails'),
      },
    ],
    CHANGE_PASSWORD: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.profileDetails'),
        onClickHandler: () => navigate(PROFILE_DETAILS),
        active: true
      },
      {
        text: t('innerLayout.settingsPage.breadcrumbs.changePassword'),
        active: false
      }
    ],
    USER_AND_PERMISSIONS: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.usersAndPerms'),
      }
    ],
     NOTIFICATIONS: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.notifications'),
        active: false
      }
    ],
    CHECKOUT_CUSTOMIZATION: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.checkoutCustomization'),
        active: false
      }
    ],
     SERVICES: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.services'),
        active: false
      }
    ],
    PAYMENT_INTEGRATIONS: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.paymentIntegrations'),
        active: false
      }
    ],
    SINGLE_PAYMENT_INTEGRATION: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.paymentIntegrations'),
        active: true,
        onClickHandler: () => navigate(PAYMENT_INTEGRATIONS)
      },
      {
        text: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.integrationDetails'),
        active: false
      }
    ],
    IFRAMES: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.iframes'),
        active: false
      }
    ],
    API_KEYS: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.apiKeys'),
        active: false
      }
    ],
    XERO: [
      sharedRoute,
      {
        text: t('innerLayout.settingsPage.breadcrumbs.xero'),
        active: false
      }
    ]
  };

  const settingsCards = [
    {
      icon: User,
      title: t('innerLayout.settingsPage.settingsCards.profileDetails.title'),
      desc: t('innerLayout.settingsPage.settingsCards.profileDetails.desc'),
      onClickHandler: () => navigate(PROFILE_DETAILS)
    },
    {
      icon: Suitcase,
      title: t('innerLayout.settingsPage.settingsCards.businessProfile.title'),
      desc: t('innerLayout.settingsPage.settingsCards.businessProfile.desc'),
      onClickHandler: () => navigate(BUSINESS_PROFILE)
    },
    ...(canViewUserManagement ?
      [{
        icon: Users,
        title: t('innerLayout.settingsPage.settingsCards.usersAndPerms.title'),
        desc: t('innerLayout.settingsPage.settingsCards.usersAndPerms.desc'),
        onClickHandler: () => navigate(USER_AND_PERMISSIONS)
      }] : []
    ),
    ...(canViewNotifications ?
      [{
        icon: Bell,
        title: t('innerLayout.settingsPage.settingsCards.notifications.title'),
        desc: t('innerLayout.settingsPage.settingsCards.notifications.desc'),
        onClickHandler: () => navigate(NOTIFICATIONS)
      }] : []
    ),
    ...(isMerchant ?
      [{
        icon: PenToSquare,
        title: t('innerLayout.settingsPage.settingsCards.checkoutCustomization.title'),
        desc: t('innerLayout.settingsPage.settingsCards.checkoutCustomization.desc'),
        onClickHandler: () => navigate(CHECKOUT_CUSTOMIZATION)
      }] : []
    ),
    ...(isMerchant ?
      [{
        icon: Bolt,
        title: t('innerLayout.settingsPage.settingsCards.services.title'),
        desc: t('innerLayout.settingsPage.settingsCards.services.desc'),
        onClickHandler: () => navigate(SERVICES)
      }] : []
    )
  ];

  const developersCards = [
    ...(canViewPaymentIntegrations ?
      [{
        icon: LinkSimple,
        title: t('innerLayout.settingsPage.developersCards.paymentIntegrations.title'),
        desc: t('innerLayout.settingsPage.developersCards.paymentIntegrations.desc'),
        onClickHandler: () => navigate(PAYMENT_INTEGRATIONS)
      }] : []
    ),
    ...(hideApiKeys
      ? []
      : [
        {
          icon: CodeSimple,
          title: t('innerLayout.settingsPage.developersCards.apiKeys.title'),
          desc: t('innerLayout.settingsPage.developersCards.apiKeys.desc'),
          onClickHandler: () => navigate(API_KEYS),
        },
      ]),
    ...(canViewIframes ?
      [{
        icon: CodeSimple,
        title: t('innerLayout.settingsPage.developersCards.iframes.title'),
        desc: t('innerLayout.settingsPage.developersCards.iframes.desc'),
        onClickHandler: () => navigate(IFRAMES)
      }] : []
    ),
    {
      icon: FileContract,
      title: t('innerLayout.settingsPage.developersCards.documentation.title'),
      desc: t('innerLayout.settingsPage.developersCards.documentation.desc'),
      onClickHandler: () => window.open(UNIFIED_DOCS_URL, '_blank')
    },
    {
      icon: Grid2Plus,
      title: t('innerLayout.settingsPage.developersCards.xero.title'),
      desc: t('innerLayout.settingsPage.developersCards.xero.desc'),
      onClickHandler: () => navigate(XERO)
    }
  ];

  return {
    internalPagesBreadcrumbs,
    settingsCards,
    developersCards
  };
};